import {
  ABILITY_KEYS,
  COINS,
  HIT_DIE_SIZES,
  SKILLS,
  STATBLOCK_DE,
  abilityMod,
  abilityScore,
  computeAc,
  computeHp,
  formatMod,
  formatStatblock,
  hasSaveProficiency,
  initiativeBonus,
  normalizeSheet,
  passivePerception,
  proficiency,
  saveBonus,
  skillBonus,
  spellcasting,
  type AbilityKey,
  type CharacterSheet,
  type NoteDetail,
  type Statblock,
} from '@kartothek/shared';
import { useState } from 'react';
import { t, tDyn } from '../i18n';
import { classNames } from '../util';
import { Inventory } from './Inventory';
import { MarkdownBody } from './MarkdownView';

const D = STATBLOCK_DE;

export function Pips(props: { total: number; filled: number; onChange?: (n: number) => void; tone?: 'ok' | 'bad' | 'slot'; label: string }) {
  return (
    <span className="pips" aria-label={props.label}>
      {Array.from({ length: props.total }, (_, i) => (
        <button
          key={i}
          type="button"
          className={classNames('pip', props.tone, i < props.filled && 'on')}
          disabled={!props.onChange}
          onClick={() => props.onChange?.(i < props.filled ? i : i + 1)}
          title={props.label}
        />
      ))}
    </span>
  );
}

function Box({ label, children, className }: { label: string; children: React.ReactNode; className?: string }) {
  return (
    <div className={classNames('cs-box', className)}>
      <div className="cs-box-value">{children}</div>
      <div className="cs-box-label">{label}</div>
    </div>
  );
}

/**
 * Charakterbogen in der Leseansicht. Werte, die sich im Spiel ändern
 * (TP, Zauberplätze, Todesrettungswürfe …), sind direkt bedienbar.
 */
export function CharacterSheetView(props: {
  note: NoteDetail;
  statblock: Statblock;
  sheet: CharacterSheet;
  defaultFormat: '2014' | '2024';
  editable: boolean;
  onSheet: (sheet: CharacterSheet) => void;
}) {
  const { note, statblock: sb, sheet } = props;
  const fm = note.frontmatter;
  const format = sheet.format ?? props.defaultFormat;
  const prof = proficiency(sb);
  const hpMax = computeHp(sb).hp;
  const hpCur = sheet.hpCurrent ?? hpMax;
  const level = Number(fm.level) || 1;
  const hitDie = sheet.hitDie ?? 8;
  const spell = spellcasting(sb, sheet, prof);
  const f = formatStatblock(sb);
  const [hpInput, setHpInput] = useState('');
  const set = (patch: Partial<CharacterSheet>) => props.onSheet(normalizeSheet({ ...sheet, ...patch }));
  // Verstorbene: Spielwerte eingefroren, Inventar/Münzen bleiben änderbar (Leichnam plündern)
  const play = props.editable && !sheet.dead;

  const applyHp = () => {
    const s = hpInput.trim();
    const m = /^([+-]?)(\d+)$/.exec(s);
    const tm = /^t(\d+)$/i.exec(s);
    if (tm) set({ hpTemp: Math.max(sheet.hpTemp ?? 0, Number(tm[1])) });
    else if (m) {
      const n = Number(m[2]);
      if (m[1] === '+') set({ hpCurrent: Math.min(hpMax, hpCur + n) });
      else {
        const temp = sheet.hpTemp ?? 0;
        const absorbed = Math.min(temp, n);
        set({ hpTemp: temp - absorbed, hpCurrent: Math.max(0, hpCur - (n - absorbed)) });
      }
    } else return;
    setHpInput('');
  };

  const sub = [
    [fm.class, fm.level ? `${t('sheet.level')} ${String(fm.level)}` : null].filter(Boolean).join(' '),
    fm.species ? String(fm.species) : null,
    fm.background ? String(fm.background) : null,
    sb.alignment || null,
  ].filter(Boolean);

  const passive = (skill: string) => {
    const s = (sb.skills ?? []).find((x) => x.name === skill);
    const stat = SKILLS.find((x) => x.name === skill)!.stat;
    return 10 + (s ? skillBonus(sb, s) : abilityMod(abilityScore(sb, stat)));
  };

  const render = (md: string) => (
    <MarkdownBody cid={note.campaignId} body={md} links={note.links} folder={note.folder} depth={2} chain={[]} isGameMaster={props.editable} preview />
  );

  return (
    <div className={`charsheet cs-${format}`}>
      <header className="cs-head">
        <div>
          <h2>{sb.name || note.title}</h2>
          <div className="muted">{sub.join(' · ')}</div>
        </div>
        <div className="cs-head-right small">
          {fm.player ? (
            <div>
              {t('sheet.player')}: <strong>{String(fm.player)}</strong>
            </div>
          ) : null}
          <div>
            {D.labels.xp}: <strong>{(sheet.xp ?? 0).toLocaleString('de-DE')}</strong>
          </div>
          <span className="cs-format">{format === '2024' ? '5e 2024' : '5e 2014'}</span>
        </div>
      </header>

      {sheet.dead && props.editable && <div className="cs-dead-hint small">✝ {t('sheet.deadLocked')}</div>}
      <div className="cs-top">
        <Box label={D.labels.ac}>{computeAc(sb).ac}</Box>
        <Box label={D.labels.initiative}>{formatMod(initiativeBonus(sb))}</Box>
        <Box label={D.labels.speed}>{f.speedText}</Box>
        <Box label={t('sheet.prof')}>{formatMod(prof)}</Box>
        <div className="cs-box cs-hp">
          <div className="cs-box-value">
            <strong className={hpCur === 0 ? 'bad' : undefined}>{hpCur}</strong>
            <span className="muted">/{hpMax}</span>
            {sheet.hpTemp ? <span className="temp-badge">+{sheet.hpTemp}</span> : null}
          </div>
          {play && (
            <input
              className="hp-input"
              value={hpInput}
              placeholder="-7 / +5 / t8"
              onChange={(e) => setHpInput(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && applyHp()}
              aria-label={t('combat.hpInput')}
            />
          )}
          <div className="cs-box-label">{D.labels.hp}</div>
        </div>
        <div className="cs-box">
          <div className="cs-box-value small">
            {level - (sheet.hitDiceUsed ?? 0)}/{level} W{hitDie}
          </div>
          {play && (
            <div className="cs-mini-buttons">
              <button disabled={(sheet.hitDiceUsed ?? 0) >= level} onClick={() => set({ hitDiceUsed: (sheet.hitDiceUsed ?? 0) + 1 })} title={t('sheet.spendHitDie')}>
                −
              </button>
              <button disabled={!sheet.hitDiceUsed} onClick={() => set({ hitDiceUsed: (sheet.hitDiceUsed ?? 0) - 1 })} title={t('sheet.regainHitDie')}>
                +
              </button>
            </div>
          )}
          <div className="cs-box-label">{t('sheet.hitDice')}</div>
        </div>
        <div className="cs-box cs-deathbox">
          <div className="cs-box-value cs-death">
            <span className="small">✓</span>
            <Pips total={3} filled={sheet.deathSaves?.success ?? 0} tone="ok" label={t('sheet.deathSuccess')} onChange={play ? (n) => set({ deathSaves: { success: n, failure: sheet.deathSaves?.failure ?? 0 } }) : undefined} />
            <span className="small">✗</span>
            <Pips total={3} filled={sheet.deathSaves?.failure ?? 0} tone="bad" label={t('sheet.deathFailure')} onChange={play ? (n) => set({ deathSaves: { failure: n, success: sheet.deathSaves?.success ?? 0 } }) : undefined} />
          </div>
          <div className="cs-box-label">{t('sheet.deathSaves')}</div>
        </div>
        <button
          className={classNames('cs-box cs-dead', sheet.dead && 'on')}
          disabled={!props.editable}
          title={sheet.dead ? t('combat.undoDead') : t('combat.dead')}
          onClick={() => {
            if (!sheet.dead && !confirm(t('combat.deadConfirm', { name: sb.name || note.title }))) return;
            set(sheet.dead ? { dead: false, deathSaves: { success: 0, failure: 0 } } : { dead: true });
          }}
        >
          <div className="cs-box-value">✝</div>
          <div className="cs-box-label">{t('combat.dead')}</div>
        </button>
        <button
          className={classNames('cs-box cs-insp', sheet.inspiration && 'on')}
          disabled={!play}
          onClick={() => set({ inspiration: !sheet.inspiration })}
        >
          <div className="cs-box-value">{sheet.inspiration ? '★' : '☆'}</div>
          <div className="cs-box-label">{format === '2024' ? t('sheet.heroicInspiration') : t('sheet.inspiration')}</div>
        </button>
        {play && (
          <button
            className="cs-box cs-rest"
            title={t('sheet.longRestHint')}
            onClick={() => {
              if (!confirm(t('sheet.longRestConfirm'))) return;
              set({
                hpCurrent: hpMax,
                hpTemp: 0,
                deathSaves: { success: 0, failure: 0 },
                hitDiceUsed: Math.max(0, (sheet.hitDiceUsed ?? 0) - Math.max(1, Math.floor(level / 2))),
                slots: sheet.slots?.map((s) => ({ ...s, used: 0 })),
              });
            }}
          >
            <div className="cs-box-value">☾</div>
            <div className="cs-box-label">{t('sheet.longRest')}</div>
          </button>
        )}
      </div>

      <div className="cs-grid">
        <section className="cs-abilities">
          {ABILITY_KEYS.map((k) => (
            <div key={k} className="cs-ability">
              <div className="cs-ability-name">{D.abilityNames[k]}</div>
              <div className="cs-ability-mod">{formatMod(abilityMod(abilityScore(sb, k)))}</div>
              <div className="cs-ability-score">{abilityScore(sb, k)}</div>
              <div className={classNames('cs-save small', hasSaveProficiency(sb, k) && 'prof')}>
                {D.labels.save} {formatMod(saveBonus(sb, k))}
              </div>
            </div>
          ))}
        </section>

        <section className="cs-skills">
          <h3>{D.labels.skills}</h3>
          <ul>
            {SKILLS.map((s) => {
              const own = (sb.skills ?? []).find((x) => x.name === s.name);
              const bonus = own ? skillBonus(sb, own) : abilityMod(abilityScore(sb, s.stat));
              const exp = own && /ex/i.test(own.note ?? '');
              return (
                <li key={s.name} className={own ? 'prof' : undefined}>
                  <span className={classNames('dot', own && 'on', exp && 'exp')} />
                  <span className="cs-skill-bonus">{formatMod(bonus)}</span> {D.skills[s.name]}{' '}
                  <span className="muted small">({D.abilities[s.stat]})</span>
                </li>
              );
            })}
          </ul>
          <div className="cs-passive small">
            <div>
              {t('sheet.passivePerception')} <strong>{passivePerception(sb)}</strong>
            </div>
            <div>
              {t('sheet.passiveInsight')} <strong>{passive('insight')}</strong>
            </div>
            <div>
              {t('sheet.passiveInvestigation')} <strong>{passive('investigation')}</strong>
            </div>
          </div>
        </section>

        <section className="cs-main">
          {(sb.actions?.length ?? 0) > 0 && (
            <div className="cs-section">
              <h3>{t('sheet.attacks')}</h3>
              {sb.actions!.map((a, i) => (
                <div key={i}>{render(`***${a.name}.*** ${a.desc}`)}</div>
              ))}
            </div>
          )}
          {format === '2024' && sheet.weaponMasteries?.length ? (
            <div className="cs-section">
              <h3>{t('sheet.weaponMasteries')}</h3>
              <ul className="plain-list">
                {sheet.weaponMasteries.map((w) => (
                  <li key={w}>{w}</li>
                ))}
              </ul>
            </div>
          ) : null}
          {spell && (
            <div className="cs-section">
              <h3>{t('sheet.spellcasting')}</h3>
              <div className="cs-spell-stats">
                <span>
                  {t('sheet.spellAbility')}: <strong>{D.abilityNames[sheet.spellAbility as AbilityKey]}</strong>
                </span>
                <span>
                  {t('sheet.spellDc')}: <strong>{spell.dc}</strong>
                </span>
                <span>
                  {t('sheet.spellAttack')}: <strong>{formatMod(spell.attack)}</strong>
                </span>
              </div>
              {sheet.slots && (
                <table className="cs-slots">
                  <tbody>
                    {sheet.slots.map((s) => (
                      <tr key={s.level}>
                        <td>{t('sheet.slotLevel', { n: s.level })}</td>
                        <td>
                          <Pips
                            total={s.total}
                            filled={s.used}
                            tone="slot"
                            label={t('sheet.slotsUsed')}
                            onChange={
                              play
                                ? (n) => set({ slots: sheet.slots!.map((x) => (x.level === s.level ? { ...x, used: n } : x)) })
                                : undefined
                            }
                          />
                        </td>
                        <td className="muted small">
                          {s.total - s.used}/{s.total}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}
          {[
            ['abilities', format === '2024' ? D.labels.traits : t('sheet.features')],
            ['bonusActions', D.labels.bonusActions],
            ['reactions', D.labels.reactions],
          ].map(([key, title]) =>
            (sb[key as 'abilities'] ?? []).length ? (
              <div key={key} className="cs-section">
                <h3>{title}</h3>
                {sb[key as 'abilities']!.map((a, i) => (
                  <div key={i}>{render(`***${a.name}.*** ${a.desc}`)}</div>
                ))}
              </div>
            ) : null,
          )}
          <div className="cs-section">
            <h3>{t('sheet.proficiencies')}</h3>
            {sheet.proficiencies ? render(sheet.proficiencies) : null}
            <div className="small">
              <strong>{D.labels.languages}:</strong> {f.lines.find((l) => l.label === D.labels.languages)?.text}
            </div>
          </div>
          <div className="cs-section">
            <h3>{t('sheet.equipment')}</h3>
            <div className="cs-coins">
              {COINS.map((c) => (
                <label key={c} className="inline">
                  <span className="cs-coin">{tDyn(`sheet.coin.${c}`)}</span>
                  {props.editable ? (
                    <input
                      className="sb-num"
                      type="number"
                      min={0}
                      defaultValue={sheet.coins?.[c] ?? ''}
                      key={`${c}-${sheet.coins?.[c] ?? 0}`}
                      onBlur={(e) => {
                        const v = e.target.value === '' ? 0 : Number(e.target.value);
                        if (v !== (sheet.coins?.[c] ?? 0)) set({ coins: { ...sheet.coins, [c]: v } });
                      }}
                    />
                  ) : (
                    <strong>{sheet.coins?.[c] ?? 0}</strong>
                  )}
                </label>
              ))}
            </div>
            {sheet.equipment ? render(sheet.equipment) : null}
          </div>
        </section>
      </div>
      <div className="cs-section cs-inventory">
        <h3>{t('inventory.title')}</h3>
        <Inventory cid={note.campaignId} items={sheet.inventory ?? []} editable={props.editable} onChange={(inventory) => set({ inventory })} />
      </div>
    </div>
  );
}

/** Bogendaten bearbeiten (Spielwerte bearbeitet der Statblock-Editor). */
export function SheetEditor(props: { sheet: CharacterSheet; defaultFormat: '2014' | '2024'; onChange: (s: CharacterSheet) => void }) {
  const { sheet } = props;
  const set = (patch: Partial<CharacterSheet>) => props.onChange(normalizeSheet({ ...sheet, ...patch }));
  const slotTotal = (lvl: number) => sheet.slots?.find((s) => s.level === lvl)?.total ?? 0;
  const setSlot = (lvl: number, total: number) => {
    const rest = (sheet.slots ?? []).filter((s) => s.level !== lvl);
    const used = Math.min(total, sheet.slots?.find((s) => s.level === lvl)?.used ?? 0);
    set({ slots: [...rest, { level: lvl, total, used }] });
  };
  return (
    <div className="sb-form sheet-editor">
      <fieldset>
        <legend>{t('sheet.title')}</legend>
        <label className="sb-field">
          <span>{t('statblock.format')}</span>
          <select value={sheet.format ?? props.defaultFormat} onChange={(e) => set({ format: e.target.value as '2014' | '2024' })}>
            <option value="2014">5e 2014</option>
            <option value="2024">5e 2024</option>
          </select>
        </label>
        <label className="sb-field">
          <span>{D.labels.xp}</span>
          <input className="sb-num" type="number" min={0} value={sheet.xp ?? ''} onChange={(e) => set({ xp: Number(e.target.value) || 0 })} />
        </label>
        <label className="sb-field">
          <span>{t('sheet.hitDie')}</span>
          <select value={sheet.hitDie ?? 8} onChange={(e) => set({ hitDie: Number(e.target.value) })}>
            {HIT_DIE_SIZES.map((d) => (
              <option key={d} value={d}>
                W{d}
              </option>
            ))}
          </select>
        </label>
        <label className="sb-field">
          <span>{t('sheet.spellAbility')}</span>
          <select value={sheet.spellAbility ?? ''} onChange={(e) => set({ spellAbility: (e.target.value || undefined) as AbilityKey | undefined })}>
            <option value="">{t('sheet.noSpellcasting')}</option>
            {ABILITY_KEYS.map((k) => (
              <option key={k} value={k}>
                {D.abilityNames[k]}
              </option>
            ))}
          </select>
        </label>
      </fieldset>
      {sheet.spellAbility && (
        <fieldset>
          <legend>{t('sheet.slotsTotal')}</legend>
          {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((lvl) => (
            <label key={lvl} className="sb-field">
              <span>{t('sheet.slotLevel', { n: lvl })}</span>
              <input className="sb-num" type="number" min={0} max={9} value={slotTotal(lvl) || ''} onChange={(e) => setSlot(lvl, Number(e.target.value) || 0)} />
            </label>
          ))}
        </fieldset>
      )}
      <fieldset>
        <legend>{t('sheet.proficiencies')}</legend>
        <textarea
          rows={3}
          defaultValue={sheet.proficiencies ?? ''}
          placeholder={t('sheet.proficienciesHint')}
          onBlur={(e) => e.target.value !== (sheet.proficiencies ?? '') && set({ proficiencies: e.target.value })}
        />
      </fieldset>
      {(sheet.format ?? props.defaultFormat) === '2024' && (
        <fieldset>
          <legend>{t('sheet.weaponMasteries')}</legend>
          <input
            className="wide-input"
            defaultValue={(sheet.weaponMasteries ?? []).join(', ')}
            placeholder={t('sheet.weaponMasteriesHint')}
            onBlur={(e) => set({ weaponMasteries: e.target.value.split(',').map((s) => s.trim()) })}
          />
        </fieldset>
      )}
      <fieldset>
        <legend>{t('sheet.equipment')}</legend>
        <textarea
          rows={4}
          defaultValue={sheet.equipment ?? ''}
          placeholder={t('sheet.equipmentHint')}
          onBlur={(e) => e.target.value !== (sheet.equipment ?? '') && set({ equipment: e.target.value })}
        />
      </fieldset>
    </div>
  );
}
